/**
 * Phase F1 — Build `local_start_date` / `local_end_date` (and optional `timezone`) for
 * GET /trades, GET /trades/statistics, GET /trades/statistics/by-symbol.
 *
 * Calendar strings must match **inclusive local calendar days in the resolved timezone**
 * (server: stored notifications TZ when `timezone` is omitted).
 *
 * @see Docs/FRONTEND_PHASE_F0_TRADE_PERIOD_CONTRACT.md
 */

import type { TradeStatisticsFilters } from "../types/trade";
import type {
  LocalCalendarDateString,
  TradePeriodLocalRangeQuery,
} from "../types/tradePeriodApiContract";
import { computeComparisonRange } from "./comparisonDateRange";
import {
  endOfLocalDay,
  lastMonthLocalRange,
  lastWeekMonSunLocalRange,
} from "./calendarWeekRanges";

export type LocalDayInput = Date | LocalCalendarDateString;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** YYYY-MM-DD using the browser's local calendar (picker at local midnight). */
export function formatDateAsCalendarDayLocal(date: Date): LocalCalendarDateString {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * YYYY-MM-DD for the instant `date` in IANA `timeZone` (e.g. stored reporting timezone).
 * Falls back to {@link formatDateAsCalendarDayLocal} if `timeZone` is invalid.
 */
export function formatDateAsCalendarDayInTimeZone(
  date: Date,
  timeZone: string
): LocalCalendarDateString {
  if (!timeZone?.trim()) {
    return formatDateAsCalendarDayLocal(date);
  }
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone.trim(),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    if (y && m && d) {
      return `${y}-${m}-${d}`;
    }
  } catch {
    // invalid IANA
  }
  return formatDateAsCalendarDayLocal(date);
}

/** YYYYMMDD integer for ordering civil dates in a zone at instant `t`. */
function zonedYmdKeyAtUtc(t: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone.trim(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(t));
  const y = +parts.find((p) => p.type === "year")!.value;
  const m = +parts.find((p) => p.type === "month")!.value;
  const d = +parts.find((p) => p.type === "day")!.value;
  return y * 10000 + m * 100 + d;
}

/**
 * UTC instant for 00:00:00.000 on calendar `ymd` (YYYY-MM-DD) in IANA `timeZone`.
 * Chart/statistics routes need ISO bounds in UTC; this matches trade log `local_*` semantics.
 */
export function startOfIanaCalendarDayUtc(ymd: LocalCalendarDateString, timeZone: string): Date {
  const zone = timeZone?.trim() || "UTC";
  const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
  const target = y * 10000 + m * 100 + d;
  let lo = Date.UTC(y, m - 1, d, 0, 0, 0, 0) - 2 * 86400000;
  let hi = Date.UTC(y, m - 1, d, 0, 0, 0, 0) + 2 * 86400000;
  for (let g = 0; g < 8; g++) {
    if (zonedYmdKeyAtUtc(lo, zone) > target) lo -= 86400000;
    if (zonedYmdKeyAtUtc(hi, zone) < target) hi += 86400000;
  }
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (zonedYmdKeyAtUtc(mid, zone) < target) lo = mid + 1;
    else hi = mid;
  }
  return new Date(lo);
}

/**
 * UTC instant for last ms (23:59:59.999) on calendar `ymd` in IANA `timeZone`.
 */
export function endOfIanaCalendarDayUtc(ymd: LocalCalendarDateString, timeZone: string): Date {
  const zone = timeZone?.trim() || "UTC";
  const start = startOfIanaCalendarDayUtc(ymd, zone).getTime();
  let t = start + 1;
  const max = start + 32 * 86400000;
  while (t < max && formatDateAsCalendarDayInTimeZone(new Date(t), zone) === ymd) {
    t += 60_000;
  }
  return new Date(t - 1);
}

/**
 * Inclusive ISO UTC range for two picker instants, using **reporting** IANA zone (not browser local).
 * Use for chart/aux statistics routes that only accept `start_date` / `end_date`.
 */
export function normalizeReportingZoneDateRangeToUtcBounds(
  startDate: Date,
  endDate: Date,
  reportingTimeZone: string
): { startDate: Date; endDate: Date } {
  const zone = reportingTimeZone?.trim() || "UTC";
  const startYmd = formatDateAsCalendarDayInTimeZone(startDate, zone);
  const endYmd = formatDateAsCalendarDayInTimeZone(endDate, zone);
  return {
    startDate: startOfIanaCalendarDayUtc(startYmd, zone),
    endDate: endOfIanaCalendarDayUtc(endYmd, zone),
  };
}

/** Monday (ISO week) civil date in `timeZone` containing `ymd`, as YYYY-MM-DD. */
export function mondayOfIsoWeekContainingYmd(
  ymd: LocalCalendarDateString,
  timeZone: string
): LocalCalendarDateString {
  const zone = timeZone?.trim() || "UTC";
  let t = startOfIanaCalendarDayUtc(ymd, zone).getTime();
  for (let i = 0; i < 14; i++) {
    const wd = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      weekday: "short",
    }).format(new Date(t));
    if (wd.startsWith("Mon")) {
      return formatDateAsCalendarDayInTimeZone(new Date(t), zone);
    }
    t -= 86400000;
  }
  return ymd;
}

/** Sunday civil date in `timeZone` for the ISO week that contains `ymd`. */
export function sundayOfIsoWeekContainingYmd(
  ymd: LocalCalendarDateString,
  timeZone: string
): LocalCalendarDateString {
  const mon = mondayOfIsoWeekContainingYmd(ymd, timeZone);
  const zone = timeZone?.trim() || "UTC";
  const mid = startOfIanaCalendarDayUtc(mon, zone).getTime() + 12 * 3600000;
  return formatDateAsCalendarDayInTimeZone(new Date(mid + 6 * 86400000), zone);
}

/** Civil date `deltaDays` before `ymd` in `timeZone` (approx. via noon anchor). */
export function subtractCalendarDaysFromYmd(
  ymd: LocalCalendarDateString,
  deltaDays: number,
  timeZone: string
): LocalCalendarDateString {
  const zone = timeZone?.trim() || "UTC";
  const mid = startOfIanaCalendarDayUtc(ymd, zone).getTime() + 12 * 3600000;
  return formatDateAsCalendarDayInTimeZone(new Date(mid - deltaDays * 86400000), zone);
}

export function addCalendarDaysToYmd(
  ymd: LocalCalendarDateString,
  deltaDays: number,
  timeZone: string
): LocalCalendarDateString {
  const zone = timeZone?.trim() || "UTC";
  const mid = startOfIanaCalendarDayUtc(ymd, zone).getTime() + 12 * 3600000;
  return formatDateAsCalendarDayInTimeZone(new Date(mid + deltaDays * 86400000), zone);
}

function normalizeLocalDayInput(
  value: LocalDayInput,
  reportingTimeZone: string
): LocalCalendarDateString {
  if (typeof value === "string") {
    const s = value.trim();
    if (!ISO_DATE.test(s)) {
      throw new Error(`Invalid calendar date (expected YYYY-MM-DD): ${value}`);
    }
    return s;
  }
  return formatDateAsCalendarDayInTimeZone(value, reportingTimeZone);
}

/** Lexicographic compare works for YYYY-MM-DD. Returns negative / 0 / positive. */
export function compareCalendarDateStrings(
  a: LocalCalendarDateString,
  b: LocalCalendarDateString
): number {
  return a.localeCompare(b);
}

/**
 * @throws Error if end is before start (calendar order).
 */
export function assertInclusiveLocalDateRange(
  local_start_date: LocalCalendarDateString,
  local_end_date: LocalCalendarDateString
): void {
  if (compareCalendarDateStrings(local_start_date, local_end_date) > 0) {
    throw new Error(
      `local_end_date (${local_end_date}) must be on or after local_start_date (${local_start_date}).`
    );
  }
}

export interface BuildTradePeriodLocalRangeOptions {
  /**
   * IANA zone used to turn `Date` pickers into YYYY-MM-DD (should match server “resolved” TZ).
   * Pass {@link useUserNotificationTimezone}'s `timezone` when available.
   */
  reportingTimeZone: string;
  /**
   * When true, include `timezone` in the query object so the server uses this IANA explicitly.
   * When false/omitted, omit `timezone` and let the API use stored Display Preferences / UTC fallback.
   */
  includeTimezoneQuery?: boolean;
}

/**
 * Produces a payload for URLSearchParams / fetch helpers. Does not set ISO `start_date`/`end_date`.
 */
export function buildTradePeriodLocalRangeQuery(
  start: LocalDayInput,
  end: LocalDayInput,
  options: BuildTradePeriodLocalRangeOptions
): TradePeriodLocalRangeQuery {
  const zone = options.reportingTimeZone?.trim() || "UTC";
  const local_start_date = normalizeLocalDayInput(start, zone);
  const local_end_date = normalizeLocalDayInput(end, zone);
  assertInclusiveLocalDateRange(local_start_date, local_end_date);

  const query: TradePeriodLocalRangeQuery = {
    local_start_date,
    local_end_date,
  };
  if (options.includeTimezoneQuery && zone) {
    query.timezone = zone;
  }
  return query;
}

/** Append local-period fields to search params (skips undefined `timezone`). */
export function appendTradePeriodLocalParams(
  params: URLSearchParams,
  period: TradePeriodLocalRangeQuery
): void {
  if (period.timezone) {
    params.set("timezone", period.timezone);
  }
  params.set("local_start_date", period.local_start_date);
  params.set("local_end_date", period.local_end_date);
}

/**
 * Shared window for GET /trades and GET /trades/statistics (local-period exit cohort).
 * Prefers `local_start_date` / `local_end_date` (+ optional `timezone` on the period) over
 * `timeframe` alone so stats and trade log stay aligned. Preset buttons (today / N-days)
 * with an empty picker still resolve to an inclusive local calendar range in `reportingTimeZone`.
 */
export type TradeListWindowQuery =
  | { kind: "local"; period: TradePeriodLocalRangeQuery }
  | {
      kind: "timeframe";
      timeframe: "today" | "7-days" | "14-days" | "30-days" | "all-time";
    };

const VALID_TRADE_TIMEFRAME = new Set([
  "today",
  "7-days",
  "14-days",
  "30-days",
  "all-time",
]);

export function resolveTradeListWindowQuery(
  dateRange: { startDate: Date | null; endDate: Date | null },
  timeframe: string,
  reportingTimeZone: string,
  options?: { includeTimezoneQuery?: boolean }
): TradeListWindowQuery {
  const zone = reportingTimeZone?.trim() || "UTC";
  const includeTz = options?.includeTimezoneQuery !== false;

  if (dateRange?.startDate && dateRange?.endDate) {
    // Date pickers represent civil calendar days. Preserve the picked YYYY-MM-DD
    // from the browser calendar instead of reinterpreting the instant in reporting TZ,
    // which can shift the day for users whose browser TZ differs from reporting TZ.
    const startYmd = formatDateAsCalendarDayLocal(dateRange.startDate);
    const endYmd = formatDateAsCalendarDayLocal(dateRange.endDate);
    return {
      kind: "local",
      period: buildTradePeriodLocalRangeQuery(
        startYmd,
        endYmd,
        { reportingTimeZone: zone, includeTimezoneQuery: includeTz }
      ),
    };
  }
  if (dateRange?.startDate || dateRange?.endDate) {
    const d = (dateRange.startDate ?? dateRange.endDate) as Date;
    const ymd = formatDateAsCalendarDayLocal(d);
    return {
      kind: "local",
      period: buildTradePeriodLocalRangeQuery(ymd, ymd, {
        reportingTimeZone: zone,
        includeTimezoneQuery: includeTz,
      }),
    };
  }

  const endYmd = formatDateAsCalendarDayInTimeZone(new Date(), zone);
  if (timeframe === "today") {
    const start = startOfIanaCalendarDayUtc(endYmd, zone);
    const end = endOfIanaCalendarDayUtc(endYmd, zone);
    return {
      kind: "local",
      period: buildTradePeriodLocalRangeQuery(start, end, {
        reportingTimeZone: zone,
        includeTimezoneQuery: includeTz,
      }),
    };
  }
  if (
    timeframe === "7-days" ||
    timeframe === "14-days" ||
    timeframe === "30-days"
  ) {
    const days = timeframe === "7-days" ? 7 : timeframe === "14-days" ? 14 : 30;
    const startYmd = subtractCalendarDaysFromYmd(endYmd, days, zone);
    const start = startOfIanaCalendarDayUtc(startYmd, zone);
    const end = endOfIanaCalendarDayUtc(endYmd, zone);
    return {
      kind: "local",
      period: buildTradePeriodLocalRangeQuery(start, end, {
        reportingTimeZone: zone,
        includeTimezoneQuery: includeTz,
      }),
    };
  }

  const tf = VALID_TRADE_TIMEFRAME.has(timeframe)
    ? (timeframe as "today" | "7-days" | "14-days" | "30-days" | "all-time")
    : "all-time";
  return { kind: "timeframe", timeframe: tf };
}

/** Inclusive day count between two YYYY-MM-DD strings (calendar order). */
export function inclusiveCalendarDaysBetween(
  startYmd: LocalCalendarDateString,
  endYmd: LocalCalendarDateString
): number {
  const toUtcMs = (ymd: LocalCalendarDateString) => {
    const [y, m, d] = ymd.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  const diffDays = (toUtcMs(endYmd) - toUtcMs(startYmd)) / 86400000;
  return Math.max(1, Math.floor(diffDays) + 1);
}

function toPreviousRollingLocalPeriod(
  startYmd: LocalCalendarDateString,
  endYmd: LocalCalendarDateString,
  zone: string,
  includeTimezoneQuery: boolean
): TradePeriodLocalRangeQuery {
  const spanDays = inclusiveCalendarDaysBetween(startYmd, endYmd);
  const prevEnd = subtractCalendarDaysFromYmd(startYmd, 1, zone);
  const prevStart = subtractCalendarDaysFromYmd(prevEnd, spanDays - 1, zone);
  return buildTradePeriodLocalRangeQuery(prevStart, prevEnd, {
    reportingTimeZone: zone,
    includeTimezoneQuery,
  });
}

/**
 * Previous comparison window for dashboard mini-stat % badges.
 * Calendar presets (this week → last week, etc.) use anchored baselines; custom ranges
 * and N-day presets use an equal-length window ending the day before the current start.
 */
export function resolveDashboardPreviousComparisonLocalRange(
  dateRange: { startDate: Date | null; endDate: Date | null },
  timeframe: string,
  reportingTimeZone: string,
  options?: { includeTimezoneQuery?: boolean }
): TradePeriodLocalRangeQuery | null {
  const zone = reportingTimeZone?.trim() || "UTC";
  const includeTimezoneQuery = options?.includeTimezoneQuery !== false;
  const periodOpts = { reportingTimeZone: zone, includeTimezoneQuery };

  switch (timeframe) {
    case "this-week": {
      const { startDate, endDate } = lastWeekMonSunLocalRange();
      return buildTradePeriodLocalRangeQuery(startDate, endDate, periodOpts);
    }
    case "last-week": {
      const last = lastWeekMonSunLocalRange();
      const prevMonday = new Date(last.startDate);
      prevMonday.setDate(prevMonday.getDate() - 7);
      const prevSunday = new Date(last.startDate);
      prevSunday.setDate(prevSunday.getDate() - 1);
      return buildTradePeriodLocalRangeQuery(prevMonday, endOfLocalDay(prevSunday), periodOpts);
    }
    case "this-month": {
      if (!dateRange.startDate || !dateRange.endDate) return null;
      const comp = computeComparisonRange(
        { startDate: dateRange.startDate, endDate: dateRange.endDate },
        "previous-month"
      );
      if (!comp?.startDate || !comp?.endDate) return null;
      return buildTradePeriodLocalRangeQuery(comp.startDate, comp.endDate, periodOpts);
    }
    case "last-month": {
      const last = lastMonthLocalRange();
      const prevStart = new Date(last.startDate.getFullYear(), last.startDate.getMonth() - 1, 1);
      const prevEnd = new Date(
        last.startDate.getFullYear(),
        last.startDate.getMonth(),
        0,
        23,
        59,
        59,
        999
      );
      return buildTradePeriodLocalRangeQuery(prevStart, prevEnd, periodOpts);
    }
    case "all-time":
      return null;
    default:
      break;
  }

  const window = resolveTradeListWindowQuery(dateRange, timeframe, zone, {
    includeTimezoneQuery,
  });
  if (window.kind === "timeframe" && window.timeframe === "all-time") {
    return null;
  }

  if (window.kind === "local") {
    return toPreviousRollingLocalPeriod(
      window.period.local_start_date,
      window.period.local_end_date,
      zone,
      includeTimezoneQuery
    );
  }

  const endYmd = formatDateAsCalendarDayInTimeZone(new Date(), zone);
  if (window.timeframe === "today") {
    const prevYmd = subtractCalendarDaysFromYmd(endYmd, 1, zone);
    return toPreviousRollingLocalPeriod(prevYmd, prevYmd, zone, includeTimezoneQuery);
  }

  const days =
    window.timeframe === "7-days" ? 7 : window.timeframe === "14-days" ? 14 : 30;
  const startYmd = subtractCalendarDaysFromYmd(endYmd, days, zone);
  return toPreviousRollingLocalPeriod(startYmd, endYmd, zone, includeTimezoneQuery);
}

/**
 * Statistics filters for the period immediately before the current dashboard window
 * (calendar preset baseline or equal-length rolling window).
 */
export function buildPreviousPeriodStatisticsFilters(
  base: TradeStatisticsFilters,
  dateRange: { startDate: Date | null; endDate: Date | null },
  timeframe: string,
  reportingTimeZone: string
): TradeStatisticsFilters | undefined {
  const zone = reportingTimeZone?.trim() || "UTC";
  const includeTimezoneQuery = Boolean(base.timezone);

  const stripWindowKeys = (filters: TradeStatisticsFilters): TradeStatisticsFilters => {
    const next = { ...filters };
    delete next.timeframe;
    delete next.local_start_date;
    delete next.local_end_date;
    delete next.start_date;
    delete next.end_date;
    delete next.previous_start_date;
    delete next.previous_end_date;
    return next;
  };

  const previous = resolveDashboardPreviousComparisonLocalRange(dateRange, timeframe, zone, {
    includeTimezoneQuery,
  });
  if (!previous) return undefined;

  return {
    ...stripWindowKeys(base),
    local_start_date: previous.local_start_date,
    local_end_date: previous.local_end_date,
    ...(previous.timezone ? { timezone: previous.timezone } : {}),
  };
}

/** Period-over-period % change for a numeric metric (null when undefined or prior is zero). */
export function percentChangeVsPrevious(
  current: number | null | undefined,
  previous: number | null | undefined
): number | null {
  if (
    current == null ||
    previous == null ||
    !Number.isFinite(current) ||
    !Number.isFinite(previous) ||
    previous === 0
  ) {
    return null;
  }
  return ((current - previous) / Math.abs(previous)) * 100;
}
